import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, CircularProgress, TextField, InputAdornment, Tabs, Tab } from "@mui/material";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";

import { axiosConfig } from "../../axios/axiosConfig";
import ReactHotToaster from "../../components/common/ReactHotToaster";

import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";

// ── palette (brand) ──────────────────────────────────────────────────────
const pageBg = "#eef2f7";
const cardBg = "#ffffff";
const cardBorder = "#e2e8f0";
const navy = "#04343a";
const brandGreen = "#016551";
const negRed = "#dc2626";
const textMain = "#1e293b";
const textMuted = "#64748b";

const OPTION_TYPE_COLORS = { CE: "#0284c7", PE: "#b45309" };
const ORDER_TYPE_COLORS = { BUY: brandGreen, SELL: negRed };

const num = (n) => (typeof n === "number" && !isNaN(n) ? n : Number(n) || 0);
const fmtRs = (n) => (n === null || n === undefined || n === "" ? "—" : `₹${num(n).toLocaleString("en-IN")}`);
const fmtPct = (n) => (n === null || n === undefined || n === "" ? "—" : `${num(n).toFixed(2)}%`);

const fmtDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime())
    ? value
    : d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const fmtExpiry = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const Chip = ({ label, color }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      fontSize: 11,
      fontWeight: 700,
      color: "#fff",
      background: color,
      borderRadius: "999px",
      padding: "3px 10px",
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </Box>
);

const COLUMNS = [
  "Symbol", "Type", "Order", "Strike Price", "Entry Price", "LTP",
  "Stop Loss", "Target", "Expected Profit", "Potential", "Lot Size",
  "Expiry", "Recommended On", "Hold By", "Action",
];

const OptionRecommendations = () => {
  const [loading, setLoading] = useState(true);
  const [trades, setTrades] = useState([]);
  const [optionType, setOptionType] = useState("All");
  const [search, setSearch] = useState("");
  const [takingId, setTakingId] = useState(null);
  const [postingId, setPostingId] = useState(null);
  const [postedIds, setPostedIds] = useState([]);

  const loadTrades = async () => {
    setLoading(true);
    try {
      const res = await axiosConfig({
        method: "get",
        url: "/option-recommendation-trades/",
      });
      setTrades(Array.isArray(res.data?.message) ? res.data.message : []);
    } catch (error) {
      console.log("option-recommendation-trades error: ", error);
      toast.error(error?.response?.data?.response || "Failed to load option recommendations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTrades();
  }, []);

  const handleTake = async (tradeId) => {
    setTakingId(tradeId);
    try {
      await axiosConfig({
        method: "post",
        url: "/option-recommendation-trades/",
        data: { trade_id: String(tradeId) },
      });
      toast.success("Trade taken successfully!");
      loadTrades();
    } catch (error) {
      console.log("take trade error: ", error);
      toast.error(error?.response?.data?.response || "Failed to take trade");
    } finally {
      setTakingId(null);
    }
  };

  const handleTradePosted = async (t) => {
    setPostingId(t.id);
    try {
      const symbolPrefix = (t.truedata_symbol || t.trade_symbol || "").match(/^[A-Za-z]+/)?.[0] || "";
      await axiosConfig({
        method: "post",
        url: "/post-external-option-future-trade/",
        data: {
          trade_symbol: t.trade_symbol,
          truedata_symbol: t.truedata_symbol,
          symbol_id: t.symbol_id,
          symbol: symbolPrefix,
          expire_date: t.expire_date,
          lot_size: t.lot_size,
          option_type: t.option_type,
          order_type: t.order_type,
          entry_price: t.entry_price,
          strike_price: t.strike_price,
          stop_loss: t.stop_loss || null,
          target_amount: t.target_amount || null,
        },
      });
      toast.success("Trade posted successfully!");
      setPostedIds((prev) => [...prev, t.id]);
    } catch (error) {
      console.log("post external option trade error: ", error);
      toast.error(error?.response?.data?.response || "Failed to post trade");
    } finally {
      setPostingId(null);
    }
  };

  const optionTypes = useMemo(
    () => ["All", ...Array.from(new Set(trades.map((t) => t.option_type))).filter(Boolean).sort()],
    [trades]
  );

  const filteredTrades = useMemo(() => {
    const q = search.trim().toLowerCase();
    return trades
      .filter((t) => optionType === "All" || t.option_type === optionType)
      .filter((t) => !q || Object.values(t || {}).some((v) => String(v ?? "").toLowerCase().includes(q)))
      .sort((a, b) => new Date(b.date_of_recommendation || 0) - new Date(a.date_of_recommendation || 0));
  }, [trades, optionType, search]);

  const handleDownload = () => {
    if (!filteredTrades.length) {
      toast.error("No recommendations to download");
      return;
    }
    const rows = filteredTrades.map((t) => ({
      Symbol: t.trade_symbol,
      "TrueData Symbol": t.truedata_symbol,
      Type: t.option_type,
      Order: t.order_type,
      "Strike Price": t.strike_price,
      "Entry Price": t.entry_price,
      LTP: t.posted_time_ltp,
      "Stop Loss": t.stop_loss,
      Target: t.target_amount,
      "Expected Profit": t.expected_profit,
      "Expected Potential (%)": t.expected_potential,
      "Lot Size": t.lot_size,
      "Expiry Date": fmtExpiry(t.expire_date),
      "Recommended On": fmtDate(t.date_of_recommendation),
      "Hold By": t.hold_by ?? "—",
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Option Recommendations");
    XLSX.writeFile(workbook, `option-recommendations-${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Downloaded successfully!");
  };

  return (
    <Box sx={{ p: 3, background: pageBg, minHeight: "100vh" }}>
      {/* Page Header */}
      <Box
        sx={{
          background: navy,
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
          p: "20px 28px",
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TrendingUpIcon sx={{ color: "#fff", fontSize: 32 }} />
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "20px" }}>
              Option Recommendations
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "13px" }}>
              All option trade recommendations
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Box
            component="button"
            onClick={loadTrades}
            sx={{
              display: "flex", alignItems: "center", gap: 0.75,
              background: "rgba(255,255,255,0.1)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px",
              padding: "9px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer",
              "&:hover": { background: "rgba(255,255,255,0.18)" },
            }}
          >
            <RefreshIcon sx={{ fontSize: 16 }} /> Refresh
          </Box>
          <Box
            component="button"
            onClick={handleDownload}
            sx={{
              display: "flex", alignItems: "center", gap: 0.75,
              background: brandGreen, color: "#fff", border: "none", borderRadius: "8px",
              padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer",
              boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)", boxShadow: "0 6px 16px rgba(1,101,81,0.4)" },
            }}
          >
            <DownloadIcon sx={{ fontSize: 16 }} /> Download Excel
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: brandGreen }} />
        </Box>
      ) : (
        <>
          {/* Filters */}
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              size="small"
              placeholder="Search symbol"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: textMuted }} />
                  </InputAdornment>
                ),
              }}
              sx={{ background: cardBg, borderRadius: "8px", minWidth: 260 }}
            />
          </Box>

          {/* Option type tabs */}
          <Box sx={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: "12px 12px 0 0", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", borderBottom: "none" }}>
            <Tabs
              value={optionType}
              onChange={(e, val) => setOptionType(val)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 44,
                px: 1,
                "& .MuiTabs-indicator": { backgroundColor: brandGreen, height: 3 },
              }}
            >
              {optionTypes.map((t) => (
                <Tab
                  key={t}
                  value={t}
                  label={t}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 13,
                    minHeight: 44,
                    color: textMuted,
                    "&.Mui-selected": { color: t === "All" ? navy : OPTION_TYPE_COLORS[t] || navy },
                  }}
                />
              ))}
            </Tabs>
          </Box>

          {/* Table */}
          <Box sx={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: "0 0 12px 12px", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>
            <Box sx={{ overflowX: "auto" }}>
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 1200 }}>
                <Box component="thead">
                  <Box component="tr">
                    {COLUMNS.map((h) => (
                      <Box
                        component="th"
                        key={h}
                        sx={{
                          textAlign: "left", fontSize: 11, color: "#fff", fontWeight: 700,
                          textTransform: "uppercase", letterSpacing: "0.5px",
                          background: navy, p: "12px 18px", whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box component="tbody">
                  {filteredTrades.length === 0 ? (
                    <Box component="tr">
                      <Box component="td" colSpan={COLUMNS.length} sx={{ p: 4, textAlign: "center", fontSize: 13, color: textMuted }}>
                        No option recommendations found
                      </Box>
                    </Box>
                  ) : (
                    filteredTrades.map((t, i) => (
                      <Box component="tr" key={t.id} sx={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                        <Box component="td" sx={{ p: "10px 18px", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
                          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: textMain }}>{t.trade_symbol}</Typography>
                          <Typography sx={{ fontSize: 10.5, color: textMuted }}>{t.truedata_symbol}</Typography>
                        </Box>
                        <Box component="td" sx={{ p: "10px 18px", borderBottom: "1px solid #f1f5f9" }}>
                          <Chip label={t.option_type} color={OPTION_TYPE_COLORS[t.option_type] || textMuted} />
                        </Box>
                        <Box component="td" sx={{ p: "10px 18px", borderBottom: "1px solid #f1f5f9" }}>
                          <Chip label={t.order_type} color={ORDER_TYPE_COLORS[t.order_type] || textMuted} />
                        </Box>
                        <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, color: textMain, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtRs(t.strike_price)}</Box>
                        <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, color: textMain, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtRs(t.entry_price)}</Box>
                        <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, color: textMain, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtRs(t.posted_time_ltp)}</Box>
                        <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, color: negRed, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtRs(t.stop_loss)}</Box>
                        <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, color: brandGreen, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtRs(t.target_amount)}</Box>
                        <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, color: textMain, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtRs(t.expected_profit)}</Box>
                        <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, fontWeight: 700, color: brandGreen, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtPct(t.expected_potential)}</Box>
                        <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, color: textMain, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{t.lot_size ?? "—"}</Box>
                        <Box component="td" sx={{ p: "10px 18px", fontSize: 12, color: textMuted, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtExpiry(t.expire_date)}</Box>
                        <Box component="td" sx={{ p: "10px 18px", fontSize: 12, color: textMuted, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtDate(t.date_of_recommendation)}</Box>
                        <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, color: textMain, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{t.hold_by ?? "—"}</Box>
                        <Box component="td" sx={{ p: "10px 18px", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, alignItems: "flex-start" }}>
                            {t.hold_by ? (
                              <Chip label="Taken" color={textMuted} />
                            ) : (
                              <Box
                                component="button"
                                onClick={() => handleTake(t.id)}
                                disabled={takingId === t.id}
                                sx={{
                                  display: "inline-flex", alignItems: "center", gap: 0.5,
                                  background: brandGreen, color: "#fff", border: "none", borderRadius: "6px",
                                  padding: "5px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                                  "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
                                  "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
                                }}
                              >
                                {takingId === t.id ? <CircularProgress size={12} sx={{ color: "#fff" }} /> : "Take"}
                              </Box>
                            )}

                            {postedIds.includes(t.id) ? (
                              <Chip label="Posted" color="#0284c7" />
                            ) : (
                              <Box
                                component="button"
                                onClick={() => handleTradePosted(t)}
                                disabled={postingId === t.id}
                                sx={{
                                  display: "inline-flex", alignItems: "center", gap: 0.5,
                                  background: "#0284c7", color: "#fff", border: "none", borderRadius: "6px",
                                  padding: "5px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                                  "&:hover": { background: "#026aa3" },
                                  "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
                                }}
                              >
                                {postingId === t.id ? <CircularProgress size={12} sx={{ color: "#fff" }} /> : "Trade Posted"}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}

      <ReactHotToaster />
    </Box>
  );
};

export default OptionRecommendations;
